import React from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  selectNotifications,
  clearNotifications,
  addRow,
  decipherTravelCostsAfterWarning,
  updateActiveModelType,
  clearTableData,
  clearAllTableData,
  setMapMode,
  updateCoordinateSystem
} from "store/reducers/inputReducer";
import {
  selectRememberedSwitch,
  setRememberedSwitch
} from "store/reducers/uiReducer.js";

import { handleExitFullScreen } from 'functions/utilities/exit_full_screen.js'

import BaseWarningDialog from "./BaseWarningDialog.jsx";


/**
 * This notification will get triggered when a row is added and there are results shown on the table.
 * It is a warning that the prior results will be cleared out.
 */

const WarningDialogActionRequired = () => {
  const dispatch = useDispatch();
  const warningNotifications = useSelector(selectNotifications("warning"));
  const isIgnored = useSelector(selectRememberedSwitch("ignoreExitExampleModeWarning"));
  const prevOpenRef = React.useRef(false);

  const activeNotification =
    warningNotifications && warningNotifications.length > 0
      ? warningNotifications[0]
      : null;

  const warningActionKey = React.useMemo(() => {
    if (!activeNotification) {
      return null;
    }

    if (activeNotification.action) {
      return activeNotification.action;
    }

    if (activeNotification.message) {
      return `message:${activeNotification.message}`;
    }

    return null;
  }, [activeNotification]);


  const openRequested = Boolean(warningNotifications && warningNotifications.length > 0);
  const isOpeningAndIgnored = Boolean(openRequested) && isIgnored && !prevOpenRef.current;

  const [dontShowAgain, setDontShowAgain] = React.useState(false);

  React.useEffect(() => {
    setDontShowAgain(isIgnored);
  }, [isIgnored, warningActionKey]);

  const lastAutoHandledRef = React.useRef(null);

  const persistDontShowAgain = React.useCallback(() => {
    if (!warningActionKey) {
      return;
    }
    dispatch(
      setRememberedSwitch({ key: "ignoreExitExampleModeWarning", value: Boolean(dontShowAgain) })
    );
  }, [dispatch, warningActionKey, dontShowAgain]);

  const handleCloseNotification = React.useCallback(() => {
    persistDontShowAgain();

    if (warningNotifications && warningNotifications.length > 0 && warningNotifications[0].action === 'addRow') {
      dispatch(addRow({...warningNotifications[0].payload, clearResults: true, resetMapLayers:true}));
    }
    else if (warningNotifications && warningNotifications.length > 0 && warningNotifications[0].action === 'decipherUploadedTravelCostPayload') {
      dispatch(decipherTravelCostsAfterWarning({...warningNotifications[0].payload}));
    }
    else if (warningNotifications && warningNotifications.length > 0 && warningNotifications[0].action === 'updateActiveModelType') {
      dispatch(updateActiveModelType({...warningNotifications[0].payload, clearResults: true, resetMapLayers:true}));
    }   

    else if (warningNotifications && warningNotifications.length > 0 && warningNotifications[0].action === 'clearTableData') {
      dispatch(clearTableData({...warningNotifications[0].payload, clearResults: true, resetMapLayers:true}));
    }

    else if (warningNotifications && warningNotifications.length > 0 && warningNotifications[0].action === 'clearAllTableData') {
      dispatch(clearAllTableData({...warningNotifications[0].payload, clearResults: true, resetMapLayers:true}));
    }

    else if (warningNotifications && warningNotifications.length > 0 && warningNotifications[0].action === 'setMapMode') {
      dispatch(setMapMode({...warningNotifications[0].payload}));
    }

    // The payload carries force:true, so the range check that raised this
    // warning is skipped on the re-dispatch and the switch goes through.
    else if (warningNotifications && warningNotifications.length > 0 && warningNotifications[0].action === 'updateCoordinateSystem') {
      dispatch(updateCoordinateSystem({...warningNotifications[0].payload}));
    }

    dispatch(clearNotifications({type:'warning'}));
  }, [persistDontShowAgain, warningNotifications, dispatch]);

  const handleCancel = () => {
    persistDontShowAgain();

    if (warningNotifications && warningNotifications.length > 0 && warningNotifications[0].action === 'updateActiveModelType') {
      dispatch(updateActiveModelType({...warningNotifications[0].cancelPayload, clearResults: false, resetMapLayers:false}));
    }
    dispatch(clearNotifications({type:'warning'}));

  };

  warningNotifications && warningNotifications.length > 0 && handleExitFullScreen();

  React.useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = openRequested;

    // Only auto-handle when the dialog is being opened AND it was already ignored.
    // This prevents the dialog from closing/proceeding immediately when the user toggles
    // "Don't show this again" while the dialog is open.
    if (!openRequested || wasOpen || !activeNotification || !warningActionKey || !isIgnored) {
      return;
    }

    const fingerprint = `${warningActionKey}:${activeNotification.message || ""}`;
    if (lastAutoHandledRef.current === fingerprint) {
      return;
    }

    lastAutoHandledRef.current = fingerprint;

    if (activeNotification.action) {
      handleCloseNotification();
      return;
    }

    dispatch(clearNotifications({ type: "warning" }));
  }, [activeNotification, warningActionKey, isIgnored, dispatch, openRequested, handleCloseNotification]);

  const handleToggleDontShowAgain = (event) => {
    const checked = Boolean(event && event.target && event.target.checked);
    setDontShowAgain(checked);
  };

  const currentMessage =
    warningNotifications && warningNotifications.length > 0
      ? warningNotifications[0].message
      : "";

  const confirmButtonText =
    (warningNotifications &&
      warningNotifications.length > 0 &&
      warningNotifications[0].proceedText) ||
    "Proceed";

  return (
    <BaseWarningDialog
      open={openRequested && !isOpeningAndIgnored}
      onClose={handleCancel}
      onConfirm={handleCloseNotification}
      title="Warning"
      message={currentMessage}
      confirmText={confirmButtonText}
      cancelText="Cancel"
      showDontShowAgain={Boolean(warningActionKey)}
      dontShowAgain={dontShowAgain}
      onToggleDontShowAgain={handleToggleDontShowAgain}
      titleId="warning-dialog-title"
      descriptionId="warning-dialog-description"
      sx={{ zIndex: 1400 }}
    />
  );
};

export default WarningDialogActionRequired;
